import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Spinner } from '../../components/Spinner';
import { TranscriptPanel } from '../../components/videos/TranscriptPanel';
import { CaptionPanel } from '../../components/videos/CaptionPanel';
import { NotesEditor } from '../../components/videos/NotesEditor';
import { TagEditor } from '../../components/videos/TagEditor';
import { VideoTitleHeader } from '../../components/videos/VideoTitleHeader';
import { PostLink } from '../../components/videos/PostLink';
import {
  useVideoQuery,
  useVideoJobsQuery,
  useVideoTagsQuery,
  useVideoTagStateQuery,
  useTranscribeVideoMutation,
  useTranslateVideoMutation,
  useUpdateVideoNotesMutation,
  useUpdateVideoTagsMutation,
  useUpdateVideoCaptionMutation,
  useUpdateVideoTitleMutation,
  useDecideSuggestionMutation,
  videoKeys,
} from '../../hooks/useVideos';
import { api } from '../../lib/api';

export default function VideoDetailPage() {
  const { stem } = useParams<{ stem: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeLang, setActiveLang] = useState<string | null>(null);

  const { data: video, isLoading, error } = useVideoQuery(stem ?? '', activeLang);
  const { data: tagVocab } = useVideoTagsQuery();
  const { data: tagState } = useVideoTagStateQuery(stem ?? '');
  const { data: jobsData } = useVideoJobsQuery({
    refetchInterval: 5_000,
    enabled:
      video?.job?.status === 'queued' ||
      video?.job?.status === 'running' ||
      video?.translation_job?.status === 'queued' ||
      video?.translation_job?.status === 'running',
  });

  // Prefetch the original-language variant so switching back feels instant.
  useQuery({
    queryKey: videoKeys.detail(stem ?? '', null),
    queryFn: () => api.getVideo(stem ?? '', null),
    enabled: Boolean(stem) && activeLang !== null,
    staleTime: STALE_TIME_MS,
  });

  const transcribe = useTranscribeVideoMutation();
  const translate = useTranslateVideoMutation();
  const notesMutation = useUpdateVideoNotesMutation(stem ?? '');
  const tagsMutation = useUpdateVideoTagsMutation(stem ?? '');
  const captionMutation = useUpdateVideoCaptionMutation(stem ?? '');
  const titleMutation = useUpdateVideoTitleMutation(stem ?? '');
  const decideSuggestion = useDecideSuggestionMutation(stem ?? '');

  // Tag provenance comes from the extended GET /:stem/tags endpoint — never
  // inferred client-side. Until it loads, the detail payload's (manual) tags
  // render exactly as they did before suggestions existed.
  const manualTags = useMemo(() => {
    if (!tagState) return video?.tags ?? [];
    return tagState.tags.filter((t) => tagState.tag_sources[t] !== 'accepted_suggestion');
  }, [tagState, video?.tags]);

  const acceptedTags = useMemo(() => {
    if (!tagState) return [];
    return tagState.tags.filter((t) => tagState.tag_sources[t] === 'accepted_suggestion');
  }, [tagState]);

  // If the server fell back to the original because the requested sidecar was
  // missing, keep the switch pointing at the original until the user picks again.
  const effectiveLang = video?.lang_fallback ? null : activeLang;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size={40} />
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
        {error?.message?.includes('404') ? 'Video not found.' : `Failed to load video: ${error?.message}`}
      </div>
    );
  }

  const currentJob = jobsData?.jobs?.find(j => j.stem === stem) ?? video.job;
  const currentTranslationJob =
    jobsData?.translation_jobs?.find(j => j.stem === stem) ?? video.translation_job;

  const handleTranscribe = async () => {
    await transcribe.mutateAsync({ stem: stem!, force: false });
    queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem!) });
  };

  const handleTranslate = async () => {
    await translate.mutateAsync({ stem: stem!, target_lang: 'en' });
    queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem!) });
  };

  const handleSaveNotes = async (body: string) => {
    await notesMutation.mutateAsync(body);
  };

  const handleTagsChange = async (tags: string[]) => {
    await tagsMutation.mutateAsync(tags);
  };

  const handleSaveCaption = async (body: string) => {
    await captionMutation.mutateAsync(body);
  };

  const handleSaveTitle = async (title: string) => {
    await titleMutation.mutateAsync(title);
  };

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/videos')}
        className="text-sm text-gray-600 hover:text-gray-900"
      >
        ← Back to library
      </button>

      <VideoTitleHeader
        key={video.title}
        title={video.title}
        filename={video.filename}
        titleSource={video.title_source}
        onSave={handleSaveTitle}
        saving={titleMutation.isPending}
        error={titleMutation.error}
      />

      <PostLink postUrl={video.post_url} />

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 space-y-6">
          <div className="bg-black rounded-lg overflow-hidden aspect-video">
            <video
              controls
              loop
              src={`/api/videos/${stem}/stream`}
              className="w-full h-full"
              poster={`/api/videos/${stem}/thumbnail`}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Filename</div>
              <div className="text-sm font-mono text-gray-900 truncate">{video.filename}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Size</div>
              <div className="text-sm text-gray-900">{video.size} bytes</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Duration</div>
              <div className="text-sm text-gray-900">
                {video.duration_seconds != null ? `${video.duration_seconds.toFixed(1)}s` : '—'}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Transcript</div>
              <div className="text-sm text-gray-900">{video.has_transcript ? 'Yes' : 'No'}</div>
            </div>
          </div>

          <CaptionPanel
            key={video.caption_raw ?? ''}
            caption={video.caption}
            captionRaw={video.caption_raw}
            captionSource={video.caption_source}
            captionMeta={video.caption_meta}
            onSave={handleSaveCaption}
            saving={captionMutation.isPending}
            error={captionMutation.error}
          />

          <TranscriptPanel
            transcript={video.transcript}
            job={currentJob}
            onTranscribe={handleTranscribe}
            languages={video.available_languages}
            activeLang={effectiveLang}
            originalLang={video.language}
            onLanguageChange={setActiveLang}
            translationJob={currentTranslationJob}
            onTranslate={handleTranslate}
          />
        </div>

        <div className="w-full lg:w-96 space-y-6">
          <NotesEditor
            key={video.notes}
            body={video.notes}
            onSave={handleSaveNotes}
            saving={notesMutation.isPending}
          />
          <TagEditor
            tags={manualTags}
            onChange={handleTagsChange}
            suggestions={tagVocab?.tags ?? []}
            acceptedTags={acceptedTags}
            pendingSuggestions={tagState?.pending_suggestions ?? []}
            onDecideSuggestion={(suggestion, decision) => decideSuggestion.mutate({ suggestion, decision })}
          />
        </div>
      </div>
    </div>
  );
}

const STALE_TIME_MS = 30_000;
